// src/constants/fishIQSoCalSpecies.ts
// Small curated SoCal coastal pack for FishIQ.
// Static/frontend only. No scraping, no backend calls, no exact spot claims.

import type { FishIQSpeciesSeed } from '../types';

export const SOCAL_FISHIQ_SPECIES_SEED: FishIQSpeciesSeed[] = [
  {
    id: 'barred_surfperch',
    slug: 'barred-surfperch',
    commonName: 'Barred Surfperch',
    scientificName: 'Amphistichus argenteus',
    family: 'surfperch',
    category: 'saltwater',
    habitats: ['surf', 'inshore', 'ocean'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A surf-zone perch often tied to troughs, cuts, sand crabs, worms, and moving beach water.',
    feedingProfile: { primaryPrey: ['sand crabs', 'worms', 'small invertebrates'], notes: 'Read wave energy, troughs, cuts, and food lanes instead of standing in one dead stretch.' },
    bestBaits: ['Sand Crab', 'Gulp Sandworm', 'Carolina Rig', 'Small Grub'],
    bestTechniques: ['Read troughs and cuts', 'Move with active water', 'Fish light Carolina rigs'],
    bestSeasons: ['spring', 'summer', 'fall', 'winter'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Find moving water and a trough first.', 'Stay mobile until bites or bait show up.'],
    advancedTips: ['Small tide-stage changes can move the bite lane.', 'Wave energy can pin fish to one narrow seam.'],
    behaviorTags: ['schooling'],
    structureTags: ['troughs', 'cuts', 'sand edges', 'current-fed bottom'],
    depthPreference: 'surf wash, troughs, and shallow sand transitions with food movement',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['surf-zone feeder', 'sand crab eater', 'mobile schooler'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'california_corbina',
    slug: 'california-corbina',
    commonName: 'California Corbina',
    scientificName: 'Menticirrhus undulatus',
    family: 'drum',
    category: 'saltwater',
    habitats: ['surf', 'inshore'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A wary surf-zone croaker that often cruises skinny water for sand crabs and worms.',
    feedingProfile: { primaryPrey: ['sand crabs', 'worms', 'small crustaceans'], notes: 'Sight windows, quiet approaches, and clean beach lanes matter a lot.' },
    bestBaits: ['Sand Crab', 'Ghost Shrimp', 'Light Carolina Rig', 'Sandworm'],
    bestTechniques: ['Sight fish shallow lanes', 'Use quiet approaches', 'Match sand crab beds'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Walk quietly and watch shallow foam lanes.', 'Use light line and natural bait profiles.'],
    advancedTips: ['Lead cruising fish instead of casting on top of them.', 'Low glare and cleaner water improve visual reads.'],
    behaviorTags: ['clear-water wary', 'scent/taste oriented'],
    structureTags: ['sand edges', 'troughs', 'swash lanes'],
    depthPreference: 'very shallow surf lanes, trough lips, and food-rich sand edges',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['wary surf cruiser', 'sand crab feeder'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'yellowfin_croaker',
    slug: 'yellowfin-croaker',
    commonName: 'Yellowfin Croaker',
    scientificName: 'Umbrina roncador',
    family: 'drum',
    category: 'saltwater',
    habitats: ['surf', 'bay', 'inshore'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A common sandy beach and bay croaker that feeds around surf lanes, worms, sand crabs, and shallow structure.',
    feedingProfile: { primaryPrey: ['sand crabs', 'worms', 'small crustaceans'], notes: 'Often a practical SoCal surf target when moving water and food line up.' },
    bestBaits: ['Sand Crab', 'Bloodworm', 'Ghost Shrimp', 'Carolina Rig'],
    bestTechniques: ['Fish troughs and cuts', 'Cover shallow beach lanes', 'Work bait-rich bay edges'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Start with natural bait around troughs.', 'Move until the beach shows bait or bites.'],
    advancedTips: ['Warmer windows can push fish shallow.', 'Bay edges and channel mouths can hold fish when surf is rough.'],
    behaviorTags: ['scent/taste oriented', 'bottom feeder'],
    structureTags: ['troughs', 'sand edges', 'bay edges'],
    depthPreference: 'shallow surf and bay-edge bottom lanes',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['croaker', 'sand feeder', 'surf/bay target'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'spotfin_croaker',
    slug: 'spotfin-croaker',
    commonName: 'Spotfin Croaker',
    scientificName: 'Roncador stearnsii',
    family: 'drum',
    category: 'saltwater',
    habitats: ['surf', 'bay', 'inshore'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A larger SoCal croaker often associated with sandy surf, bays, channels, and natural baits.',
    feedingProfile: { primaryPrey: ['clams', 'worms', 'crustaceans'], notes: 'Bottom texture, food, and moving water shape the read more than exact points.' },
    bestBaits: ['Ghost Shrimp', 'Bloodworm', 'Clam', 'Sand Crab'],
    bestTechniques: ['Soak natural bait near sand edges', 'Target bay channel edges', 'Fish beach troughs'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Use natural bait and keep it near bottom.', 'Check surf troughs and bay edges first.'],
    advancedTips: ['Food-rich hard-to-soft bottom transitions can repeat.', 'Tide movement can tighten the bite window.'],
    behaviorTags: ['scent/taste oriented', 'bottom feeder'],
    structureTags: ['sand edges', 'channel edges', 'troughs'],
    depthPreference: 'bottom lanes along sandy surf, bay channels, and current-fed edges',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['croaker', 'bottom feeder', 'natural bait target'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'spotted_bay_bass',
    slug: 'spotted-bay-bass',
    commonName: 'Spotted Bay Bass',
    scientificName: 'Paralabrax maculatofasciatus',
    aliases: ['Spottie'],
    family: 'sea bass',
    category: 'saltwater',
    habitats: ['bay', 'estuary', 'inshore'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A structure-loving bay bass often tied to docks, eelgrass, riprap, moorings, and tidal current seams.',
    feedingProfile: { primaryPrey: ['baitfish', 'shrimp', 'small crabs'], notes: 'Current, shade, eelgrass, docks, and hard structure are the first reads.' },
    bestBaits: ['Paddle Tail', 'Swimbait', 'Jerk Shad', 'Creature Bait'],
    bestTechniques: ['Work docks and moorings', 'Fish eelgrass edges', 'Target current seams'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Start around docks, riprap, and eelgrass edges.', 'Use small swimbaits and cover water.'],
    advancedTips: ['Tide angle can reposition fish tightly on structure.', 'Small current seams can outperform obvious-looking water.'],
    behaviorTags: ['ambush predator', 'current-aware feeder'],
    structureTags: ['docks', 'eelgrass', 'riprap', 'tide seams'],
    depthPreference: 'shallow-to-mid bay structure and current-fed edges',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['bay bass', 'structure-oriented', 'ambush feeder'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'barred_sand_bass',
    slug: 'barred-sand-bass',
    commonName: 'Barred Sand Bass',
    scientificName: 'Paralabrax nebulifer',
    family: 'sea bass',
    category: 'saltwater',
    habitats: ['bay', 'inshore', 'ocean'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A sandy-bottom bass that uses bay mouths, reefs, structure edges, and bait-rich coastal lanes.',
    feedingProfile: { primaryPrey: ['anchovy', 'sardine', 'squid', 'small baitfish'], notes: 'Structure plus sand transition is the core read.' },
    bestBaits: ['Swimbait', 'Leadhead Squid', 'Jig', 'Live Bait'],
    bestTechniques: ['Slow-roll sand edges', 'Fish reef-to-sand transitions', 'Work bay-mouth structure'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Keep presentations near bottom.', 'Look for sand next to structure.'],
    advancedTips: ['Bait pushes and current direction can change productive edges.', 'Do not ignore subtle bottom relief.'],
    behaviorTags: ['ambush predator', 'bait-tracker'],
    structureTags: ['sand edges', 'reef edges', 'bay mouths', 'drop-offs'],
    depthPreference: 'bottom-oriented sand and structure transitions',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['sand bass', 'bottom ambusher'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'kelp_bass',
    slug: 'kelp-bass',
    commonName: 'Kelp Bass / Calico Bass',
    scientificName: 'Paralabrax clathratus',
    aliases: ['Calico Bass', 'Kelp Bass'],
    family: 'sea bass',
    category: 'saltwater',
    habitats: ['inshore', 'ocean', 'bay'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A kelp and hard-structure bass that ambushes bait around edges, boilers, reefs, and coastal cover.',
    feedingProfile: { primaryPrey: ['anchovy', 'sardine', 'squid', 'small baitfish'], notes: 'Kelp edges, hard structure, bait, and surge/current drive the read.' },
    bestBaits: ['Swimbait', 'Surface Iron', 'Jerk Shad', 'Live Bait'],
    bestTechniques: ['Fish kelp edges', 'Work reef structure', 'Target bait and current lanes'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Start around visible kelp or hard structure.', 'Use weedless or structure-friendly presentations when needed.'],
    advancedTips: ['Current pushing bait into kelp edges can create short hot windows.', 'Boiler rocks and shade lines can hold better fish.'],
    behaviorTags: ['ambush predator', 'bait-tracker'],
    structureTags: ['kelp edges', 'reefs', 'boilers', 'hard structure'],
    depthPreference: 'kelp, reef, and nearshore structure edges',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['kelp bass', 'structure ambusher'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'pacific_mackerel',
    slug: 'pacific-mackerel',
    commonName: 'Pacific Mackerel',
    scientificName: 'Scomber japonicus',
    family: 'mackerel',
    category: 'saltwater',
    habitats: ['ocean', 'inshore', 'bay'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A fast schooling bait-chaser that can show around piers, harbors, beaches, and nearshore bait schools.',
    feedingProfile: { primaryPrey: ['anchovy', 'sardine', 'small baitfish'], notes: 'Find bait, birds, current edges, and active surface lanes.' },
    bestBaits: ['Small Jig', 'Sabiki', 'Spoon', 'Small Swimbait'],
    bestTechniques: ['Cast to bait schools', 'Work piers and harbor mouths', 'Retrieve small metals quickly'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Look for bait and surface activity.', 'Small shiny lures are a simple start.'],
    advancedTips: ['Schools move fast, so stay mobile.', 'Current seams can hold bait in reachable lanes.'],
    behaviorTags: ['schooling', 'bait-tracker', 'roaming predator'],
    structureTags: ['bait schools', 'piers', 'harbor mouths', 'current seams'],
    depthPreference: 'tracks bait from surface lanes to mid-depth schools',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['schooling', 'bait tracker'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
  {
    id: 'pacific_bonito',
    slug: 'pacific-bonito',
    commonName: 'Pacific Bonito',
    scientificName: 'Sarda chiliensis',
    family: 'mackerel',
    category: 'saltwater',
    habitats: ['ocean', 'inshore', 'bay'],
    regions: ['West Coast', 'Southern California'],
    summary: 'A hard-fighting schooling predator that follows anchovies, sardines, warm-water pushes, and active current edges.',
    feedingProfile: { primaryPrey: ['anchovy', 'sardine', 'small baitfish'], notes: 'Warm-water pushes, bait, birds, and surface activity are stronger signals than exact shoreline labels.' },
    bestBaits: ['Small Metal Jig', 'Bonito Feather', 'Spoon', 'Surface Iron'],
    bestTechniques: ['Cast to breaking fish', 'Work harbor mouths and piers', 'Retrieve fast through bait lanes'],
    bestSeasons: ['summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Look for birds, bait, and surface flashes.', 'Keep a fast-moving lure ready.'],
    advancedTips: ['Schools can appear and vanish quickly.', 'Current plus bait can briefly make one lane much better than the rest.'],
    behaviorTags: ['schooling', 'bait-tracker', 'roaming predator'],
    structureTags: ['bait schools', 'harbor mouths', 'piers', 'current seams'],
    depthPreference: 'surface to mid-depth bait lanes near active current or warm pushes',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['schooling predator', 'warm-water target'],
    editorialSources: [{ title: 'Roddo SoCal coastal seed v1', sourceType: 'editorial' }],
  },
];

export default SOCAL_FISHIQ_SPECIES_SEED;
